'use client';

import React from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const MAX_WIDTH_CLASSES = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-lg',
  xl: 'sm:max-w-xl',
  '2xl': 'sm:max-w-2xl',
  '3xl': 'sm:max-w-3xl',
  '4xl': 'sm:max-w-4xl',
} as const;

type MaxWidth = keyof typeof MAX_WIDTH_CLASSES;

interface BaseDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  children: React.ReactNode;

  /**
   * When provided, the body and footer are wrapped in a `<form>` element and
   * the confirm button becomes `type="submit"`. Pass `form.handleSubmit(...)`
   * from react-hook-form or a plain submit handler here.
   */
  onSubmit?: (e: React.FormEvent<HTMLFormElement>) => void;

  /** Label for the primary action button. If omitted, the button is not rendered. */
  confirmLabel?: string;
  /** Label shown on the confirm button while `confirmLoading` is true. */
  confirmLoadingLabel?: string;
  /** Optional icon rendered to the left of the confirm label. */
  confirmIcon?: React.ReactNode;
  confirmVariant?: 'default' | 'destructive';
  confirmDisabled?: boolean;
  confirmLoading?: boolean;
  /**
   * Click handler for the confirm button. Not needed when `onSubmit` is used
   * (the submit button will invoke the form handler).
   */
  onConfirm?: () => void;

  cancelLabel?: string;
  /** Hide the cancel button. */
  hideCancel?: boolean;

  /** Content rendered on the left side of the footer (e.g. destructive actions). */
  footerExtra?: React.ReactNode;
  /** Fully custom footer content. Overrides the built-in cancel/confirm buttons. */
  footer?: React.ReactNode;
  /** Hide the footer entirely. */
  hideFooter?: boolean;

  /** Preset maximum width. Defaults to `md`. */
  maxWidth?: MaxWidth;
  /** Extra classes applied to the DialogContent. Merged via tailwind-merge. */
  className?: string;
  /** Extra classes applied to the body wrapper. */
  bodyClassName?: string;
  /**
   * When true, switches the dialog to a flex-column layout where the header
   * and footer are pinned and the body grows to fill and scrolls. Combine
   * with a `className` like `h-[80vh]` or `max-h-[85vh]` to set the height.
   * If the body hosts its own scroll container (e.g. `<ScrollArea>`), pass
   * `bodyClassName="overflow-hidden"` to disable the body's built-in scroll.
   */
  scrollableBody?: boolean;

  preventCloseOnOutsideClick?: boolean;
  preventCloseOnEscape?: boolean;
}

export function BaseDialog({
  isOpen,
  onOpenChange,
  title,
  description,
  children,
  onSubmit,
  confirmLabel,
  confirmLoadingLabel,
  confirmIcon,
  confirmVariant = 'default',
  confirmDisabled,
  confirmLoading,
  onConfirm,
  cancelLabel = 'Cancel',
  hideCancel,
  footerExtra,
  footer,
  hideFooter,
  maxWidth = 'md',
  className,
  bodyClassName,
  scrollableBody,
  preventCloseOnOutsideClick,
  preventCloseOnEscape,
}: BaseDialogProps) {
  const body = (
    <div
      className={cn(
        'space-y-4',
        scrollableBody && 'flex-1 min-h-0 overflow-y-auto',
        bodyClassName,
      )}
    >
      {children}
    </div>
  );

  const builtInFooter = !hideFooter && (confirmLabel || !hideCancel) && (
    <DialogFooter className={cn(scrollableBody && 'flex-shrink-0')}>
      {footerExtra && <div className="mr-auto">{footerExtra}</div>}
      {!hideCancel && (
        <Button
          type="button"
          variant="ghost"
          onClick={() => onOpenChange(false)}
          disabled={confirmLoading}
        >
          {cancelLabel}
        </Button>
      )}
      {confirmLabel && (
        <Button
          type={onSubmit ? 'submit' : 'button'}
          variant={confirmVariant}
          onClick={onSubmit ? undefined : onConfirm}
          disabled={confirmDisabled || confirmLoading}
        >
          {confirmLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {confirmLoadingLabel ?? confirmLabel}
            </>
          ) : (
            <>
              {confirmIcon && <span className="mr-2 inline-flex">{confirmIcon}</span>}
              {confirmLabel}
            </>
          )}
        </Button>
      )}
    </DialogFooter>
  );

  const footerNode = hideFooter ? null : footer ?? builtInFooter;

  const inner = (
    <>
      {body}
      {footerNode}
    </>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          MAX_WIDTH_CLASSES[maxWidth],
          scrollableBody && 'flex flex-col overflow-hidden',
          className,
        )}
        preventCloseOnOutsideClick={preventCloseOnOutsideClick}
        preventCloseOnEscape={preventCloseOnEscape}
      >
        <DialogHeader className={cn(scrollableBody && 'flex-shrink-0')}>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {onSubmit ? (
          <form
            onSubmit={onSubmit}
            className={cn(
              scrollableBody
                ? 'flex-1 flex flex-col gap-4 min-h-0'
                : 'space-y-4',
            )}
          >
            {inner}
          </form>
        ) : (
          inner
        )}
      </DialogContent>
    </Dialog>
  );
}
